<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <MeuLinkConvite :codigo-indicacao="codigoIndicacao" />

        <div class="card">
          <!-- Card header -->
          <div class="pb-0 card-header">
            <div class="d-lg-flex">
              <div>
                <h5 class="mb-0">Rede Estruturada</h5>
                <p class="mb-0 text-sm">Visualização em árvore da sua rede de afiliados</p>
              </div>
              <div class="my-auto mt-4 ms-auto mt-lg-0">
                <div class="my-auto ms-auto">
                  <span v-if="!loadingAuth && !loading && membroId && flatNodes.length > 1"
                        class="badge bg-gradient-info text-sm">
                    <i class="fas fa-sitemap me-1"></i>
                    {{ flatNodes.length - 1 }} {{ flatNodes.length - 1 === 1 ? 'indicado' : 'indicados' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="card-body">
            <!-- Loading -->
            <div v-if="loadingAuth || loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Carregando...</span>
              </div>
              <p class="text-sm text-secondary mt-3 mb-0">Carregando rede...</p>
            </div>

            <!-- Erro -->
            <div v-else-if="erroAuth" class="alert alert-warning text-sm" role="alert">
              <i class="fas fa-exclamation-triangle me-2"></i>
              <strong>Erro de autenticação:</strong> {{ erroAuth }}
            </div>

            <!-- Não sincronizado -->
            <div v-else-if="naoSincronizado" class="empty-state">
              <div class="empty-icon bg-gradient-warning">
                <i class="fas fa-sync-alt"></i>
              </div>
              <h6 class="mt-3">Você ainda não está na rede</h6>
              <p class="text-sm text-secondary mb-0">
                Peça ao gestor para sincronizar os treinadores do Laravel
              </p>
            </div>

            <!-- Árvore vazia -->
            <div v-else-if="!flatNodes.length || (flatNodes.length === 1 && downline.length === 0)" class="empty-state">
              <div class="empty-icon bg-gradient-primary">
                <i class="fas fa-tree"></i>
              </div>
              <h6 class="mt-3">Você ainda não tem indicados</h6>
              <p class="text-sm text-secondary mb-0">
                Compartilhe seu link de convite acima para começar a crescer sua rede
              </p>
            </div>

            <!-- Árvore SVG com pan + zoom -->
            <div v-else class="tree-wrapper">
              <!-- Controles -->
              <div class="controles">
                <button class="btn-controle" @click="zoomIn" title="Ampliar (+)">
                  <i class="fas fa-plus"></i>
                </button>
                <button class="btn-controle" @click="zoomOut" title="Reduzir (−)">
                  <i class="fas fa-minus"></i>
                </button>
                <button class="btn-controle" @click="resetarView" title="Centralizar">
                  <i class="fas fa-crosshairs"></i>
                </button>
                <span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
              </div>

              <svg
                ref="svgEl"
                class="arvore-svg"
                :class="{ 'cursor-grabbing': arrastando }"
                @wheel.prevent="aoRodarMouse"
                @mousedown="iniciarArrasto"
                @mousemove="moverArrasto"
                @mouseup="finalizarArrasto"
                @mouseleave="finalizarArrasto"
              >
                <g :transform="`translate(${tx}, ${ty}) scale(${scale})`">
                  <g class="edges">
                    <line v-for="(e, i) in edges" :key="'e' + i"
                          :x1="e.x1" :y1="e.y1" :x2="e.x2" :y2="e.y2"
                          stroke="#cbd5e1" stroke-width="2" />
                  </g>
                  <g class="nodes">
                    <g v-for="no in flatNodes" :key="no.membroId"
                       :transform="`translate(${no.x}, ${no.y})`"
                       class="no-item">
                      <circle :r="28"
                              :fill="no.isCentral ? '#5e72e4' : '#ffffff'"
                              :stroke="no.isCentral ? '#fb6340' : '#5e72e4'"
                              stroke-width="2.5" />
                      <text text-anchor="middle" y="5"
                            :fill="no.isCentral ? '#ffffff' : '#344767'"
                            font-size="12" font-weight="700"
                            style="pointer-events: none; user-select: none;">
                        {{ iniciais(no.nome) }}
                      </text>
                      <text text-anchor="middle" :y="46" fill="#344767" font-size="11" font-weight="600"
                            style="pointer-events: none; user-select: none;">
                        {{ truncar(no.nome, 14) }}
                      </text>
                      <text text-anchor="middle" :y="60" fill="#67748e" font-size="9"
                            style="pointer-events: none; user-select: none;">
                        {{ no.codigoIndicacao }}
                      </text>
                    </g>
                  </g>
                </g>
              </svg>

              <div class="legenda mt-3">
                <span class="legenda-item">
                  <span class="dot dot-central"></span>
                  Você
                </span>
                <span class="legenda-item">
                  <span class="dot dot-filho"></span>
                  Indicados
                </span>
                <span class="dica-uso">
                  <i class="fas fa-hand-paper me-1"></i>
                  Arraste para mover · Scroll para zoom
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import redeMarketingApi from '@/services/api/redeMarketingRepository'
import { useMembroLogado } from './useMembroLogado'
import MeuLinkConvite from './MeuLinkConvite.vue'

const { loading: loadingAuth, erro: erroAuth, membroId, codigoIndicacao, naoSincronizado, garantir } = useMembroLogado()

const loading = ref(false)
const noCentral = ref(null)
const downline = ref([])
const svgEl = ref(null)

// Pan & zoom state
const tx = ref(0)
const ty = ref(0)
const scale = ref(1)
const arrastando = ref(false)
const arrasteInicial = ref({ x: 0, y: 0, tx: 0, ty: 0 })

const MIN_SCALE = 0.3
const MAX_SCALE = 3

const tree = computed(() => {
  if (!noCentral.value) return null
  const root = {
    membroId: noCentral.value.membroId,
    nome: noCentral.value.nomeMembro,
    codigoIndicacao: noCentral.value.codigoIndicacao,
    children: [],
    isCentral: true,
  }
  const mapa = new Map()
  mapa.set(root.membroId, root)
  const nivelCentral = noCentral.value.nivel ?? 0
  const ordenada = [...downline.value].sort((a, b) => a.nivel - b.nivel)
  for (const n of ordenada) {
    const nodo = {
      membroId: n.membroId,
      nome: n.nomeMembro,
      codigoIndicacao: n.codigoIndicacao,
      nivel: n.nivel - nivelCentral,
      children: [],
      isCentral: false,
    }
    mapa.set(nodo.membroId, nodo)
    const pai = mapa.get(n.patrocinadorId)
    ;(pai || root).children.push(nodo)
  }
  return root
})

const flatNodes = computed(() => {
  const out = []
  edgesRef.value = []
  if (!tree.value || !tree.value.membroId) return out

  const hSpacing = 100
  const vSpacing = 120

  function largura(no) {
    if (no.children.length === 0) { no._w = hSpacing; return hSpacing }
    let tot = 0
    for (const c of no.children) tot += largura(c)
    no._w = Math.max(tot, hSpacing)
    return no._w
  }
  function posicionar(no, x, y) {
    no.x = x; no.y = y
    out.push(no)
    if (no.children.length === 0) return
    let fx = x - no._w / 2
    for (const c of no.children) {
      fx += c._w / 2
      posicionar(c, fx, y + vSpacing)
      edgesRef.value.push({ x1: x, y1: y + 28, x2: c.x, y2: y + vSpacing - 28 })
      fx += c._w / 2
    }
  }

  largura(tree.value)
  posicionar(tree.value, Math.max(tree.value._w / 2, 150), 60)
  return out
})

const edgesRef = ref([])
const edges = computed(() => { flatNodes.value; return edgesRef.value })

function iniciais(n) {
  if (!n) return '?'
  const p = n.trim().split(/\s+/)
  return (p.length === 1 ? p[0].substring(0, 2) : p[0][0] + p[p.length - 1][0]).toUpperCase()
}
function truncar(s, m) { return !s ? '' : (s.length > m ? s.substring(0, m - 1) + '…' : s) }

// ===== Pan =====
function iniciarArrasto(evt) {
  arrastando.value = true
  arrasteInicial.value = {
    x: evt.clientX,
    y: evt.clientY,
    tx: tx.value,
    ty: ty.value,
  }
}
function moverArrasto(evt) {
  if (!arrastando.value) return
  const dx = evt.clientX - arrasteInicial.value.x
  const dy = evt.clientY - arrasteInicial.value.y
  tx.value = arrasteInicial.value.tx + dx
  ty.value = arrasteInicial.value.ty + dy
}
function finalizarArrasto() {
  arrastando.value = false
}

// ===== Zoom =====
function aplicarZoom(novoScale, pivoClienteX, pivoClienteY) {
  const limitado = Math.max(MIN_SCALE, Math.min(MAX_SCALE, novoScale))
  if (!svgEl.value) {
    scale.value = limitado
    return
  }
  // Mantem o ponto sob o cursor fixo durante o zoom
  const rect = svgEl.value.getBoundingClientRect()
  const px = pivoClienteX - rect.left
  const py = pivoClienteY - rect.top
  const svgX = (px - tx.value) / scale.value
  const svgY = (py - ty.value) / scale.value
  scale.value = limitado
  tx.value = px - svgX * limitado
  ty.value = py - svgY * limitado
}
function aoRodarMouse(evt) {
  const delta = -evt.deltaY * 0.001
  aplicarZoom(scale.value * (1 + delta), evt.clientX, evt.clientY)
}
function zoomIn() {
  if (!svgEl.value) return
  const rect = svgEl.value.getBoundingClientRect()
  aplicarZoom(scale.value * 1.2, rect.left + rect.width / 2, rect.top + rect.height / 2)
}
function zoomOut() {
  if (!svgEl.value) return
  const rect = svgEl.value.getBoundingClientRect()
  aplicarZoom(scale.value * 0.8, rect.left + rect.width / 2, rect.top + rect.height / 2)
}
function resetarView() {
  scale.value = 1
  centralizar()
}
function centralizar() {
  if (!svgEl.value || flatNodes.value.length === 0) return
  const rect = svgEl.value.getBoundingClientRect()
  const xs = flatNodes.value.map(n => n.x)
  const ys = flatNodes.value.map(n => n.y)
  const minX = Math.min(...xs) - 60
  const maxX = Math.max(...xs) + 60
  const minY = Math.min(...ys) - 60
  const maxY = Math.max(...ys) + 80
  const larguraArvore = maxX - minX
  const alturaArvore = maxY - minY

  const escalaX = rect.width / larguraArvore
  const escalaY = rect.height / alturaArvore
  scale.value = Math.min(escalaX, escalaY, 1)
  tx.value = (rect.width - larguraArvore * scale.value) / 2 - minX * scale.value
  ty.value = (rect.height - alturaArvore * scale.value) / 2 - minY * scale.value
}

async function carregar() {
  if (!membroId.value) return
  loading.value = true
  try {
    const [noResp, downResp] = await Promise.all([
      redeMarketingApi.BuscarNo(membroId.value),
      redeMarketingApi.Downline(membroId.value),
    ])
    noCentral.value = noResp.data
    downline.value = downResp.data
    await nextTick()
    centralizar()
  } catch (e) {
    console.warn('[RedeEstruturada] erro:', e.message)
  } finally {
    loading.value = false
  }
}

watch(membroId, (v) => { if (v) carregar() })

onMounted(async () => {
  await garantir()
  if (membroId.value) carregar()
})
</script>

<style scoped>
.empty-state {
  text-align: center;
  padding: 40px 20px;
}
.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.6rem;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}

.tree-wrapper {
  position: relative;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e9ecef;
}

.controles {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  padding: 6px 10px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
}
.btn-controle {
  width: 30px;
  height: 30px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  color: #5e72e4;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  transition: all 0.15s;
}
.btn-controle:hover {
  background: #5e72e4;
  color: #fff;
  border-color: #5e72e4;
}
.zoom-info {
  font-size: 0.78rem;
  color: #67748e;
  font-weight: 600;
  padding: 0 6px;
  min-width: 44px;
  text-align: center;
}

.arvore-svg {
  width: 100%;
  height: 520px;
  display: block;
  cursor: grab;
  user-select: none;
  touch-action: none;
}
.arvore-svg.cursor-grabbing {
  cursor: grabbing;
}

.no-item circle {
  transition: filter 0.15s, stroke-width 0.15s;
}
.no-item:hover circle {
  stroke-width: 3.5;
  filter: drop-shadow(0 4px 10px rgba(94, 114, 228, 0.35));
}

.legenda {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
  font-size: 0.78rem;
  color: #67748e;
}
.legenda-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dica-uso {
  margin-left: auto;
  font-style: italic;
  font-size: 0.75rem;
  color: #94a3b8;
}
.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}
.dot-central {
  background: #5e72e4;
  border: 2px solid #fb6340;
}
.dot-filho {
  background: #fff;
  border: 2px solid #5e72e4;
}
</style>
